import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type UserRole } from './AuthContext';

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    // iter-42: previously rendered an sr-only "Loading…" inside a flex
    // container — visually a blank white screen, which during the redirect
    // loop felt indistinguishable from a crash. A visible spinner makes the
    // intermediate state legible without changing routing semantics.
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-screen items-center justify-center bg-gradient-to-b from-[#F5EFE7] to-white"
      >
        <span
          aria-hidden
          className="block h-10 w-10 animate-spin rounded-full border-[3px] border-[#9C7848]/25 border-t-[#9C7848]"
        />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/auth/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export function RequireRole({
  roles,
  children,
  fallback = '/app/home',
}: {
  roles: UserRole[];
  children: ReactNode;
  /**
   * iter-38: where to redirect when the current user's role isn't in
   * `roles`. Defaults to /app/home (the pilgrim shell, useful when a
   * pilgrim accidentally lands on a volunteer-only route). Override
   * with /volunteer when wrapping the pilgrim shell so volunteers
   * don't end up on /app/* (which leaks the pilgrim Settings/Health
   * Card menu — the volunteer has neither).
   */
  fallback?: string;
}): JSX.Element {
  const { user, status } = useAuth();

  if (status !== 'authenticated' || !user) {
    return <Navigate to="/auth/login" replace />;
  }

  // iter-42 / iter-45: a null role used to ping-pong between /app/home
  // and /volunteer forever. We break that loop, but ONLY when the user is
  // genuinely still early in onboarding — pre-role-selection.
  //
  // If they've already submitted /profile/complete (state is 'health' or
  // 'complete'), bouncing them to /profile/complete mid-flow loses the
  // health-card data they just typed. In that case the backend is the
  // one out of sync (returning a partial /auth/me without `role`), so we
  // trust the session and render. Backend endpoints individually enforce
  // authorization, so a "wrong-shell" leak can't actually do harm.
  if (!user.role) {
    switch (user.onboarding_state) {
      case 'language':
      case 'profile':
      case 'role':
        return <Navigate to="/profile/complete" replace />;
      default:
        return <>{children}</>;
    }
  }

  if (!roles.includes(user.role)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

export function RequireOnboardingComplete({ children }: { children: ReactNode }): JSX.Element {
  const { user, status } = useAuth();

  if (status !== 'authenticated' || !user) {
    return <Navigate to="/auth/login" replace />;
  }

  switch (user.onboarding_state) {
    case 'language':
      return <Navigate to="/language" replace />;
    case 'profile':
      return <Navigate to="/profile/complete" replace />;
    case 'role':
      // iter-25: /profile/complete now captures name + age + gender + role
      // in one form, so the dedicated /profile/type picker would be a
      // duplicate step. Funnel both 'profile' and 'role' through the
      // combined screen. /profile/type stays as a settings/edit path
      // (e.g., changing role later) but is no longer the onboarding fork.
      return <Navigate to="/profile/complete" replace />;
    case 'health':
      return user.role === 'volunteer' ? <>{children}</> : <Navigate to="/health/onboarding" replace />;
    case 'complete':
    default:
      return <>{children}</>;
  }
}
